import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router-dom'
import { appRoutePath } from 'src/feature/router/util/app-route-path'
import { getColorByRating } from '../../util/skillGapUtils'

export type SkillData = {
  skillName: string
  avgLevel: number
  expectedLevel: number
}

export type SkillsTableProps = {
  skills: SkillData[]
  isLoading: boolean
  title?: string
}

export const SkillsTable = observer(({ skills, isLoading, title }: SkillsTableProps) => {
  const navigate = useNavigate()
  const { translation } = useLocalizationStore()

  if (isLoading) {
    return (
      <AppSkeleton
        count={1}
        direction="column"
        rects={[
          { width: '100%', height: 40 },
          { width: '100%', height: 400 },
        ]}
      />
    )
  }

  if (!skills || skills.length === 0) {
    return (
      <AppBox sx={styles.tableContainer}>
        {title && <Caption sx={styles.tableTitle}>{title}</Caption>}
        <AppPaper sx={styles.emptyStateContainer}>
          <Caption sx={styles.emptyText}>
            {translation.pages.myTeam.metricLabels.noSkillsDataAvailable()}
          </Caption>
        </AppPaper>
      </AppBox>
    )
  }

  return (
    <AppBox sx={styles.tableContainer}>
      {title && <Caption sx={styles.tableTitle}>{title}</Caption>}
      <AppBox sx={styles.tableScrollContainer}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><Typography sx={{ fontSize: { xs: 11, sm: 12 }, fontWeight: 600 }}>{translation.pages.myTeam.metricLabels.skill()}</Typography></TableCell>
              <TableCell><Typography sx={{ fontSize: { xs: 11, sm: 12 }, fontWeight: 600 }}>{translation.pages.myTeam.metricLabels.ratingAvg()}</Typography></TableCell>
              <TableCell><Typography sx={{ fontSize: { xs: 11, sm: 12 }, fontWeight: 600 }}>{translation.pages.myTeam.metricLabels.expectedAvg()}</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {skills.map((skill) => {
              return (
                <TableRow
                  key={skill.skillName}
                  hover
                  onClick={() => navigate(appRoutePath.myTeamSkillGaps(skill.skillName))}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography sx={{ fontSize: { xs: 11, sm: 12 } }}>{skill.skillName}</Typography>
                  </TableCell>
                  <TableCell>
                    <AppBox sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, alignItems: 'center' }}>
                      {[1, 2, 3, 4, 5].map((star) => {
                        const isSelected = star === Math.floor(skill.avgLevel)

                        return (
                          <AppBox
                            key={star}
                            sx={{
                              width: { xs: 25, sm: 30 },
                              height: isSelected ? { xs: 18, sm: 20 } : { xs: 12, sm: 15 },
                              borderRadius: '10px',
                              backgroundColor: isSelected ? getColorByRating(skill.avgLevel) : 'background.dark',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease',
                            }}
                          >
                            {isSelected && (
                              <Typography sx={{ fontSize: { xs: 9, sm: 10 }, fontWeight: 500, color: 'primary.contrastText' }}>
                                {skill.avgLevel.toFixed(2)}
                              </Typography>
                            )}
                          </AppBox>
                        )
                      })}
                    </AppBox>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: { xs: 11, sm: 12 } }}>{skill.expectedLevel.toFixed(2)}</Typography>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </AppBox>
    </AppBox>
  )
})

const styles = {
  tableContainer: {
    flexDirection: 'column',
    width: '100%',
    gap: 5,
  },
  tableScrollContainer: {
    width: '100%',
    maxHeight: '400px',
    overflowY: 'auto',
    overflowX: 'auto',
    '& .MuiTable-root': {
      minWidth: { xs: 500, sm: 'auto' },
    },
  },
  tableTitle: {
    fontSize: { xs: 11, md: 12 },
    color: 'text.secondary',
    fontWeight: 500,
  },
  emptyStateContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    minHeight: '400px',
    padding: { xs: 2, sm: 3 },
    backgroundColor: 'background.dark',
    borderRadius: '10px',
  },
  emptyText: {
    fontSize: 11,
    color: 'text.secondary',
    textAlign: 'center',
  },
}
